import React, { useState } from 'react';
import { resetPassword } from '../../../../api/authApi';
import { useAuth } from '../../../../AuthContext';
import { useProfileSheet } from '../../../../contexts/ProfileSheetContext';
import { validatePassword } from '../../../../utils/passwordValidation';
import SheetField from '../shared/SheetField';
import SheetMessage from '../shared/SheetMessage';
import PasswordStrengthMeter from '../shared/PasswordStrengthMeter';

interface ResetPasswordViewProps {
  /** Hands the one-shot success banner to the login view. */
  onComplete: (notice: string) => void;
}

/** Password reset — the /reset-password?token= emailed deep link (1h token). */
const ResetPasswordView: React.FC<ResetPasswordViewProps> = ({ onComplete }) => {
  const { token, setView } = useProfileSheet();
  const { login } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ [k: string]: string }>({});
  const [error, setError] = useState<string | null>(null);
  // Derived, not captured at mount: the deep-link open can deliver the token
  // a render after the view mounts — deriving lets the view heal.
  const [apiTokenError, setApiTokenError] = useState(false);
  const tokenError = apiTokenError || !token;
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: { [k: string]: string } = {};
    const pwErr = validatePassword(password);
    if (pwErr) errs.password = pwErr;
    if (confirm !== password) errs.confirm = 'Passwords do not match';
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0 || !token) return;

    setError(null);
    setSubmitting(true);
    try {
      const result = await resetPassword(token, password);
      if (result.username) {
        // The token proved control of the email — sign them straight in with
        // the password they just chose (auth-success effect handles landing).
        try {
          await login(result.username, password);
          return;
        } catch {
          // Fall through to the manual sign-in path.
        }
      }
      onComplete('Password updated — sign in with your new password.');
      setView('login');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      if (/token|expired|invalid/i.test(message)) {
        setApiTokenError(true);
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (tokenError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <SheetMessage kind="err">
          This reset link is invalid or has expired.
        </SheetMessage>
        <div className="tend-links-row">
          <button type="button" className="tend-link" onClick={() => setView('forgot')}>
            Request a new reset link
          </button>
          <button type="button" className="tend-link" onClick={() => setView('login')}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {error && <SheetMessage kind="err">{error}</SheetMessage>}
      <SheetField
        label="New password"
        value={password}
        onChange={setPassword}
        type="password"
        name="new-password"
        autoComplete="new-password"
        error={fieldErrors.password}
        disabled={submitting}
        autoFocus
      />
      <PasswordStrengthMeter password={password} />
      <SheetField
        label="Confirm new password"
        value={confirm}
        onChange={setConfirm}
        type="password"
        name="confirm-password"
        autoComplete="new-password"
        error={fieldErrors.confirm}
        disabled={submitting}
      />
      <button
        type="submit"
        className="tend-btn tend-btn--cta tend-btn--block"
        disabled={submitting}
        style={{ marginTop: 4 }}
      >
        {submitting ? 'Updating…' : 'Update Password'}
      </button>
    </form>
  );
};

export default ResetPasswordView;
